import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Mail, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Button, Input } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const { completeSession } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const hasAutoSubmitted = useRef(false);

  useEffect(() => {
    const paramEmail = searchParams.get('email') ?? '';
    const paramCode = searchParams.get('code') ?? '';
    const stateEmail = (location.state as any)?.email ?? '';

    if (paramEmail) setEmail(paramEmail);
    else if (stateEmail) setEmail(stateEmail);

    if (paramCode) setCode(paramCode.trim());
  }, [searchParams, location.state]);

  useEffect(() => {
    const paramEmail = searchParams.get('email') ?? '';
    const paramCode = searchParams.get('code') ?? '';
    const fromLink = paramEmail && paramCode && !hasAutoSubmitted.current;

    if (fromLink) {
      hasAutoSubmitted.current = true;
      setEmail(paramEmail);
      setCode(paramCode.trim());
      setError('');
      setLoading(true);
      api
        .post('/auth/verify-email', { email: paramEmail, code: paramCode.trim() })
        .then(async (response) => {
          setSuccess(true);
          const token = response.data.data?.token;
          if (token) {
            const hasShop = await completeSession(token, { email: paramEmail });
            setTimeout(() => {
              navigate(hasShop ? '/' : '/create-shop', { replace: true });
            }, 1500);
          } else {
            setTimeout(() => navigate('/login', { replace: true }), 1500);
          }
        })
        .catch((err: any) => {
          setError(err.response?.data?.message || 'Invalid verification code. Please try again.');
          setLoading(false);
        });
    }
  }, [searchParams, navigate, completeSession]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !code) {
      setError('Please enter your email and verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/verify-email', {
        email,
        code,
      });

      const token = response.data.data?.token;
      if (!token) {
        setError('Verification succeeded but no session was returned. Please sign in.');
        return;
      }

      setSuccess(true);
      const hasShop = await completeSession(token, { email });
      setTimeout(() => {
        navigate(hasShop ? '/' : '/create-shop', { replace: true });
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setResending(true);
    setError('');

    try {
      await api.post('/auth/resend-verification', { email });
      setError(''); // Clear any previous errors
      alert('Verification code has been resent to your email');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend verification code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {success ? (
              <CheckCircle className="text-green-500" size={64} />
            ) : (
              <Mail className="text-custom-primary" size={64} />
            )}
          </div>
          <h1 className="text-4xl font-bold text-custom-primary mb-2">
            {success ? 'Email Verified!' : 'Verify Your Email'}
          </h1>
          <p className="text-gray-600">
            {success
              ? 'Your email has been verified successfully!'
              : 'Please enter the verification code sent to your email'}
          </p>
        </div>

        <div className="card p-8">
          {success ? (
            <div className="text-center">
              <p className="text-gray-600 mb-4">Redirecting you...</p>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-primary mx-auto"></div>
            </div>
          ) : (
            <form onSubmit={handleVerify} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
                  <XCircle size={20} />
                  <span>{error}</span>
                </div>
              )}

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                icon={Mail}
                iconPosition="left"
                required
              />

              <Input
                label="Verification Code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Enter 6-digit code"
                maxLength={6}
                required
              />

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={loading}
              >
                Verify Email
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="text-sm text-custom-primary hover:underline flex items-center justify-center space-x-2 mx-auto"
                >
                  <RefreshCw size={16} className={resending ? 'animate-spin' : ''} />
                  <span>{resending ? 'Resending...' : "Didn't receive code? Resend"}</span>
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;

